import { useState, useContext } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import Select from 'react-select'
import makeAnimated from 'react-select/animated'
import { toast } from 'react-toastify'

import { useAppSelector } from '../../../../../hooks/useReduxHooks'
import type { CustomUser, ModalOptions, OptionType } from '../../../../../interfaces'
import { selectAuth } from '../../../../../store/slices'
import {
    useAssignRoleToUserMutation,
    useCreateUserMutation,
    useGetRolesQuery,
    useRemoveRoleFromUserMutation,
    useUpdateUserMutation,
} from '../../../../../store/api/identia'
import { AuthContext } from '../../../../../context/auth/AuthProvider'
import SpinnerModal from '../../../../general/spinner/SpinnerModal'

const animatedComponents = makeAnimated()

interface FormUser {
    user: string
    name: string
    lastname: string
    email: string
    phone: string
    dni: string
    pass: string
    rol: OptionType[] | undefined
    address: string
    description: string
    photo: FileList | string | undefined
}

interface AddEditUserProps {
    showModal: ModalOptions
    setShowModal: React.Dispatch<React.SetStateAction<ModalOptions>>
    currentUser?: CustomUser
    setCurrentUser: React.Dispatch<React.SetStateAction<CustomUser | undefined>>
}

export const AddEditUser = ({
    showModal,
    setShowModal,
    currentUser,
    setCurrentUser,
}: AddEditUserProps) => {
    console.log({ currentUser })
    const { entity } = useAppSelector(selectAuth)
    const { user: authContext } = useContext(AuthContext)
    const platformId = authContext.platform.id || 'multiedu'
    const activeEntityId = entity || authContext.dataEntity?._id || ''

    // const [varchar, setVarchar] = useState<any>(400)
    const [showPassword, setShowPassword] = useState<boolean>(false)

    const { register, handleSubmit, control, watch, setValue } = useForm<FormUser>({
        defaultValues: {
            user: showModal === 'edit' ? currentUser?._id : '',
            name: showModal === 'edit' ? currentUser?.name : '',
            lastname: showModal === 'edit' ? currentUser?.lastname : '',
            email: showModal === 'edit' ? currentUser?.email : '',
            phone: showModal === 'edit' ? currentUser?.phone : '',
            dni: showModal === 'edit' ? currentUser?.dni : '',
            photo: showModal === 'edit' ? currentUser?.photo : undefined,
            pass: '',
            rol:
                showModal === 'edit'
                    ? currentUser?.roles.map((r) => ({
                          value: r.id || r._id,
                          label: r.name || (typeof r.rol === 'object' ? (r.rol as any).rol || (r.rol as any).name : r.rol) || '',
                      }))
                    : undefined,
            address: showModal === 'edit' ? currentUser?.address || '' : '',
            description: showModal === 'edit' ? currentUser?.description || '' : '',
        },
    })

    const { roles, isLoading: isLoadingRoles } = useGetRolesQuery(
        {
            context_type: 'platform',
            context_id: platformId,
        },
        {
            selectFromResult: ({ data, isLoading }) => ({
                roles:
                    data?.map((r) => ({
                        value: r.id,
                        label: r.name,
                    })) || [],
                isLoading,
            }),
        },
    )

    const [createUser, { isLoading: isCreating }] = useCreateUserMutation()
    const [updateUser, { isLoading: isUpdating }] = useUpdateUserMutation()
    const [assignRoleToUser, { isLoading: isAssigning }] = useAssignRoleToUserMutation()
    const [removeRoleFromUser, { isLoading: isRemoving }] = useRemoveRoleFromUserMutation()

    const isLoading = isCreating || isUpdating || isLoadingRoles || isAssigning || isRemoving

    const onSubmit: SubmitHandler<FormUser> = async (data) => {
        if (showModal === 'add' && data.pass.length < 6)
            return toast.warning('La contraseña debe tener al menos 6 caracteres')
        if (!data.rol || !data.rol.length) return toast.warning('Seleccione un rol')

        try {
            if (showModal === 'add') {
                // Crear el usuario con roles y contexto en un solo paso atómico
                await createUser({
                    attributes: {
                        name: data.name,
                        lastname: data.lastname,
                        email: data.email,
                        phone: data.phone,
                        dni: data.dni,
                        address: data.address,
                        description: data.description,
                        password: data.pass,
                    },
                    platform_id: platformId,
                    entity_id: activeEntityId,
                    role_id: data.rol?.[0]?.value,
                }).unwrap()

                toast.success('Usuario creado y roles asignados correctamente')
                closeModal()
            } else {
                // --- Lógica de EDICIÓN ---
                const attributes: any = {}
                
                // Solo mandamos atributos que han cambiado
                if (data.name !== currentUser?.name) attributes.name = data.name
                if (data.lastname !== currentUser?.lastname) attributes.lastname = data.lastname
                if (data.email !== currentUser?.email) attributes.email = data.email
                if (data.phone !== currentUser?.phone) attributes.phone = data.phone
                if (data.dni !== currentUser?.dni) attributes.dni = data.dni
                if (data.address !== (currentUser?.address || '')) attributes.address = data.address
                if (data.description !== (currentUser?.description || '')) attributes.description = data.description

                // Si hay una nueva contraseña
                if (data.pass && data.pass.length >= 6) attributes.password = data.pass

                if (Object.keys(attributes).length > 0) {
                    const updatePayload: any = {
                        userId: currentUser?._id!,
                        attributes,
                        platformId,
                    }

                    await updateUser(updatePayload).unwrap()
                }

                const currentRoleIds: string[] = (currentUser?.roles || [])
                    .map((r: any) => r.id || r._id)
                    .filter(Boolean)

                const newRoleIds: string[] = (data.rol || [])
                    .map((r: any) => r.value)
                    .filter(Boolean)

                const rolesToRemove = currentRoleIds.filter(id => !newRoleIds.includes(id))
                const rolesToAdd = newRoleIds.filter(id => !currentRoleIds.includes(id))

                if (rolesToRemove.length > 0 || rolesToAdd.length > 0) {
                    for (const roleId of rolesToRemove) {
                        await removeRoleFromUser({
                            userId: currentUser?._id!,
                            roleId,
                            context_type: 'entity',
                            context_id: activeEntityId,
                        }).unwrap()
                    }

                    for (const roleId of rolesToAdd) {
                        await assignRoleToUser({
                            userId: currentUser?._id!,
                            role_id: roleId,
                            context_type: 'entity',
                            context_id: activeEntityId,
                        }).unwrap()
                    }
                }

                toast.success('Usuario actualizado correctamente')
                closeModal()
            }
        } catch (error: any) {
            console.error('Error en el proceso de usuario:', error)
            toast.error(error.data?.message || `Error al ${showModal === 'add' ? 'crear' : 'actualizar'} usuario`)
        }
    }

    const closeModal = () => setShowModal('hide')

    return (
        <div className="main_AddUser">
            <div className="headAddUser">
                <h3>{showModal === 'edit' ? 'Editar' : 'Crear'} usuario</h3>
                <i className="fas fa-times-circle" onClick={closeModal} />
            </div>
            <form className="bodyAddUser" onSubmit={handleSubmit(onSubmit)}>
                <div className="contPhotoUser">
                    <div className="contImgUser">
                        <label
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                gap: '1rem',
                            }}
                        >
                            {watch('photo') && watch('photo.length') ? (
                                typeof watch('photo') === 'object' ? (
                                    <img
                                        src={URL.createObjectURL(
                                            (watch('photo') as FileList)[0],
                                        )}
                                        alt={currentUser?.name}
                                        onError={({ currentTarget }) => {
                                            currentTarget.onerror = null
                                            currentTarget.src =
                                                'https://via.placeholder.com/30'
                                        }}
                                        className="imgDeforView"
                                    />
                                ) : (
                                    <img
                                        src={watch('photo') as string}
                                        alt={currentUser?.name}
                                        onError={({ currentTarget }) => {
                                            currentTarget.onerror = null
                                            currentTarget.src =
                                                'https://via.placeholder.com/30'
                                        }}
                                        className="imgDeforView"
                                    />
                                )
                            ) : (
                                <div className="imgDeforView">
                                    <i className="fas fa-camera" />
                                </div>
                            )}
                            Establecer foto de perfil
                            <input
                                type="file"
                                accept=".png, .jpg, .jpeg"
                                {...register('photo', {
                                    onChange: (
                                        e: React.ChangeEvent<HTMLInputElement>,
                                    ) => {
                                        if (e.target.files) {
                                            const typeFile = e.target.files[0].type
                                            if (
                                                typeFile === 'image/png' ||
                                                typeFile === 'image/jpg' ||
                                                typeFile === 'image/jpeg'
                                            ) {
                                                setValue('photo', {
                                                    ...(e.target.files as FileList),
                                                    length: e.target.files.length,
                                                })
                                            } else {
                                                setValue('photo', undefined)
                                                toast.warning(
                                                    'Formato de imagen no válido',
                                                )
                                            }
                                        }
                                    },
                                })}
                            />
                        </label>
                    </div>
                </div>
                <div className="contFormCour">
                    <div className="contFormCour scrollBody scroll">
                        <div className="sepContAddUser">
                            <div className="contInAddUser">
                                <p>
                                    <b>Nombre *</b>
                                </p>
                                <input
                                    type="text"
                                    required
                                    disabled={isLoading}
                                    {...register('name')}
                                />
                            </div>
                            <div className="contInAddUser">
                                <p>
                                    <b>Apellidos *</b>
                                </p>
                                <input
                                    type="text"
                                    required
                                    disabled={isLoading}
                                    {...register('lastname')}
                                />
                            </div>
                        </div>
                        <div className="contInAddUser">
                            <p>
                                <b>Correo electrónico *</b>
                            </p>
                            <input
                                type="email"
                                required
                                disabled={isLoading}
                                {...register('email')}
                            />
                        </div>
                        <div className="contInAddUser">
                            <p>
                                <b>Dirección</b>
                            </p>
                            <input
                                type="text"
                                disabled={isLoading}
                                {...register('address')}
                            />
                        </div>
                        <div className="sepContAddUser">
                            <div className="contInAddUser">
                                <p>
                                    <b>Celular</b>
                                </p>
                                <input
                                    type="text"
                                    maxLength={9}
                                    disabled={isLoading}
                                    {...register('phone')}
                                />
                            </div>
                            <div className="contInAddUser">
                                <p>
                                    <b>DNI</b>
                                </p>
                                <input
                                    type="text"
                                    maxLength={8}
                                    disabled={isLoading}
                                    {...register('dni')}
                                />
                            </div>
                        </div>
                        <div className="sepContAddUser">
                            <div className="contInAddUser">
                                <p>
                                    <b>Seleccione rol de usuario *</b>
                                </p>
                                <Controller
                                    name="rol"
                                    control={control}
                                    // rules={{ required: true }}
                                    render={({ field: { value, ...rest } }) => (
                                        <Select
                                            closeMenuOnSelect={false}
                                            components={animatedComponents}
                                            className="selectbox"
                                            maxHeight={250}
                                            options={roles}
                                            placeholder={'Selecione rol...'}
                                            value={watch('rol')}
                                            isMulti
                                            maxMenuHeight={120}
                                            isLoading={isLoadingRoles}
                                            loadingMessage={() => 'Cargando...'}
                                            noOptionsMessage={() =>
                                                'No hay docentes disponibles'
                                            }
                                            {...rest}
                                        />
                                    )}
                                />
                            </div>
                            {showModal === 'add' && (
                                <div className="contInAddUser">
                                    <p>
                                        <b>Contraseña *</b>
                                    </p>
                                    <input
                                        type={`${showPassword ? 'text' : 'password'}`}
                                        required
                                        disabled={isLoading}
                                        maxLength={30}
                                        {...register('pass')}
                                    />
                                    <i
                                        className={`far fa-eye${
                                            showPassword ? '-slash' : ''
                                        }`}
                                        onClick={() => setShowPassword(!showPassword)}
                                    />
                                </div>
                            )}
                        </div>
                        <div className="contInAddUser">
                            <p>
                                <b>Descripción del usuario</b>
                            </p>
                            <textarea
                                style={{ height: '63px' }}
                                maxLength={400}
                                disabled={isLoading}
                                {...register('description')}
                            />
                            <p
                                style={{
                                    textAlign: 'right',
                                    color: '#a29f9f',
                                    fontWeight: 600,
                                }}
                            >
                                {/* {varchar} caracteres */}
                            </p>
                        </div>
                    </div>
                    <div className="optsBtnAdd">
                        <button onClick={closeModal} disabled={isLoading}>
                            Cancelar
                        </button>
                        <button type="submit" disabled={isLoading}>
                            {isLoading ? <SpinnerModal /> : 'Guardar'}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    )
}
